// Pure helpers for WhatsApp template audience rules.
//
// Deliberately dependency-free (no MUI, no redux, no react) so it can be unit
// tested without dragging the component barrel in — importing that barrel makes
// a Jest suite hang on this project.
//
// Mirrors apps/whatsapp/audience.py. This file NEVER decides whether a send is
// allowed — the backend does that at the send boundary and returns
// `is_eligible_for_lead` / `audience_blocked_reason` on the template payload.
// Everything here is presentation: labels, chips, and shaping the PATCH body.

// Feature slug seeded by permissions/0025, disabled on every practice until a
// superadmin turns it on. EVERY audience surface must be gated on this — with
// it off the user sees exactly the UI they saw before this feature existed.
export const AUDIENCE_FEATURE = 'whatsapp-template-audience';

export const AUDIENCE_POLICY = {
    INHERIT: 'INHERIT',
    CUSTOM: 'CUSTOM',
};

export const TREATMENT_RULE_MODE = {
    ALL: 'ALL',
    ALLOW: 'ALLOW',
    BLOCK: 'BLOCK',
};

// Matches apps.leads.choices.LEAD_HEALTHCARE_PROVIDER_CHOICES.
export const HEALTHCARE_PROVIDERS = [
    { value: 'NHS', label: 'NHS' },
    { value: 'PRIVATE', label: 'Private' },
    { value: 'OTHER', label: 'Other' },
];

export const ALL_PROVIDER_VALUES = HEALTHCARE_PROVIDERS.map((p) => p.value);

// The permissive rule — what an unconfigured template resolves to.
export const EMPTY_RULE = {
    audience_policy: AUDIENCE_POLICY.INHERIT,
    audience_healthcare_providers: [],
    treatment_rule_mode: TREATMENT_RULE_MODE.ALL,
    treatment_rule_categories: [],
    treatment_rule_items: [],
    send_when_treatment_unknown: false,
};

const asArray = (v) => (Array.isArray(v) ? v : []);

/** Normalise a template (or a partial form value) into the full rule shape. */
export const toRuleValue = (source) => ({
    audience_policy: source?.audience_policy || AUDIENCE_POLICY.INHERIT,
    audience_healthcare_providers: asArray(source?.audience_healthcare_providers),
    treatment_rule_mode: source?.treatment_rule_mode || TREATMENT_RULE_MODE.ALL,
    treatment_rule_categories: asArray(source?.treatment_rule_categories),
    treatment_rule_items: asArray(source?.treatment_rule_items),
    send_when_treatment_unknown: Boolean(source?.send_when_treatment_unknown),
});

/**
 * Rule that actually governs a template.
 *
 * The backend already computes this and returns it as `effective_audience_rule`
 * (using the practice-default keys, without the `audience_` prefix). Fall back
 * to the template's own fields when that annotation is absent — e.g. payloads
 * from an endpoint that predates it.
 */
export const effectiveRule = (template) => {
    const fromBackend = template?.effective_audience_rule;
    if (fromBackend && typeof fromBackend === 'object') {
        return {
            audience_healthcare_providers: asArray(fromBackend.healthcare_providers),
            treatment_rule_mode: fromBackend.treatment_rule_mode || TREATMENT_RULE_MODE.ALL,
            treatment_rule_categories: asArray(fromBackend.treatment_rule_categories),
            treatment_rule_items: asArray(fromBackend.treatment_rule_items),
            send_when_treatment_unknown: Boolean(fromBackend.send_when_treatment_unknown),
        };
    }
    const own = toRuleValue(template);
    return own.audience_policy === AUDIENCE_POLICY.CUSTOM ? own : toRuleValue(null);
};

/**
 * True when the rule cannot block anything.
 *
 * An allow-list naming EVERY provider is semantically identical to an empty
 * one. The backend normalises that to `[]` on save (`_validate_providers`) and
 * the editor normalises it on toggle, so it should not reach here — but a value
 * written straight through the API can, and a chip reading "NHS, Private, Other
 * only" for a rule that blocks nobody would be a lie.
 */
export const providersAreUnrestricted = (providers) => {
    const list = asArray(providers);
    return list.length === 0 || ALL_PROVIDER_VALUES.every((p) => list.includes(p));
};

export const isPermissive = (rule) => (
    providersAreUnrestricted(rule?.audience_healthcare_providers)
    && (rule?.treatment_rule_mode || TREATMENT_RULE_MODE.ALL) === TREATMENT_RULE_MODE.ALL
);

/**
 * One-glance label for the Audience column and the preview footnote.
 *
 * Short by design — this renders inside a table chip, so it stays under ~20
 * characters. The full rule is one click away in the drawer.
 */
export const audienceSummary = (template) => {
    const rule = effectiveRule(template);
    const inherited = (template?.audience_policy || AUDIENCE_POLICY.INHERIT) === AUDIENCE_POLICY.INHERIT;

    if (isPermissive(rule)) {
        return { label: 'Everyone', restricted: false, inherited };
    }

    const parts = [];
    const providers = asArray(rule.audience_healthcare_providers);
    if (!providersAreUnrestricted(providers)) {
        const excluded = ALL_PROVIDER_VALUES.filter((p) => !providers.includes(p));
        // "No NHS" reads better than "Private, Other" when exactly one type is
        // excluded — which is the common case this feature was built for.
        if (excluded.length === 1) {
            parts.push(`No ${HEALTHCARE_PROVIDERS.find((p) => p.value === excluded[0])?.label}`);
        } else {
            // "NHS" alone is ambiguous — it could read as "NHS is blocked".
            // The stored list is an ALLOW-list, so say so.
            parts.push(`${providers
                .map((p) => HEALTHCARE_PROVIDERS.find((x) => x.value === p)?.label || p)
                .join(', ')} only`);
        }
    }

    const count = asArray(rule.treatment_rule_categories).length
        + asArray(rule.treatment_rule_items).length;
    if (rule.treatment_rule_mode === TREATMENT_RULE_MODE.ALLOW && count) {
        parts.push(`Only ${count} treatment${count === 1 ? '' : 's'}`);
    } else if (rule.treatment_rule_mode === TREATMENT_RULE_MODE.BLOCK && count) {
        parts.push(`Excludes ${count} treatment${count === 1 ? '' : 's'}`);
    }

    // A rule with a mode set but nothing selected produces no parts. The
    // BACKEND treats that half-saved state as unconfigured and enforces
    // nothing, so the chip must say the same thing — otherwise the grid claims
    // a template is restricted while every send goes through.
    if (parts.length === 0) {
        return { label: 'Everyone', restricted: false, inherited };
    }

    return { label: parts.join(' · '), restricted: true, inherited };
};

/**
 * Short snackbar copy for a blocked send, keyed by the backend's block code.
 *
 * The picker already shows WHY inline, so by the time a send is refused the
 * operator has either bypassed the UI or is on a stale client — they need a
 * quick confirmation, not a paragraph. The long-form reason stays on the
 * template row's info tooltip.
 */
export const AUDIENCE_BLOCKED_MESSAGE = 'This template cannot be used for this Lead';

export const shortAudienceReason = () => AUDIENCE_BLOCKED_MESSAGE;

/** Join a list the way a person would: "A", "A and B", "A, B and C". */
export const humanJoin = (items, conjunction = 'and') => {
    const list = asArray(items).filter(Boolean);
    if (list.length === 0) return '';
    if (list.length === 1) return String(list[0]);
    if (list.length === 2) return `${list[0]} ${conjunction} ${list[1]}`;
    return `${list.slice(0, -1).join(', ')} ${conjunction} ${list[list.length - 1]}`;
};

/**
 * Resolve the selected category/item ids to display names, capped so a rule
 * naming twenty treatments does not produce a paragraph.
 */
export const selectedTreatmentNames = (rule, nameIndex = {}, cap = 3) => {
    const names = [
        ...asArray(rule?.treatment_rule_categories).map((id) => nameIndex[`c${id}`]),
        ...asArray(rule?.treatment_rule_items).map((id) => nameIndex[`i${id}`]),
    ].filter(Boolean);
    if (names.length <= cap) return { shown: names, extra: 0 };
    return { shown: names.slice(0, cap), extra: names.length - cap };
};

/**
 * One plain-English sentence describing exactly who receives this template.
 *
 * This is the control that makes the rest of the editor legible — radio buttons
 * and checkboxes describe the CONTROLS, this describes the CONSEQUENCE. Written
 * to be readable aloud, because that is the test of whether an operator will
 * trust what the rule does.
 */
export const audienceSentence = (rule, nameIndex = {}) => {
    if (isPermissive(rule)) return 'Sent to every patient.';

    const providers = asArray(rule?.audience_healthcare_providers);
    const providerNames = providers
        .map((p) => HEALTHCARE_PROVIDERS.find((x) => x.value === p)?.label || p);
    const audience = providersAreUnrestricted(providers)
        ? 'every patient'
        : `${humanJoin(providerNames)} patients`;

    const mode = rule?.treatment_rule_mode || TREATMENT_RULE_MODE.ALL;
    const { shown, extra } = selectedTreatmentNames(rule, nameIndex);
    const hasTreatmentRule = mode !== TREATMENT_RULE_MODE.ALL && shown.length > 0;

    if (!hasTreatmentRule) return `Sent to ${audience}.`;

    const treatmentList = humanJoin(shown, mode === TREATMENT_RULE_MODE.ALLOW ? 'or' : 'or')
        + (extra ? ` and ${extra} more` : '');

    if (mode === TREATMENT_RULE_MODE.BLOCK) {
        return `Sent to ${audience}, except anyone whose treatment is ${treatmentList}.`;
    }
    const tail = rule?.send_when_treatment_unknown
        ? ' Patients with no treatment recorded are included.'
        : ' Patients with no treatment recorded are excluded.';
    return `Sent to ${audience} whose treatment is ${treatmentList}.${tail}`;
};

/** Count how many treatments sit under each category, for the picker. */
export const treatmentCountByCategory = (practiceTreatmentResults) => {
    const counts = {};
    (practiceTreatmentResults || []).forEach((row) => {
        const categoryId = row?.treatment_item?.treatment_category?.id;
        if (categoryId == null) return;
        counts[categoryId] = (counts[categoryId] || 0) + 1;
    });
    return counts;
};

/** Long-form description for tooltips and the practice-default card. */
export const audienceDescription = (rule, { treatmentNameById } = {}) => {
    if (isPermissive(rule)) return 'Sent to every patient.';

    const lines = [];
    const providers = asArray(rule.audience_healthcare_providers);
    if (!providersAreUnrestricted(providers)) {
        lines.push(`Only ${providers
            .map((p) => HEALTHCARE_PROVIDERS.find((x) => x.value === p)?.label || p)
            .join(', ')} patients.`);
    }

    const ids = [
        ...asArray(rule.treatment_rule_categories).map((id) => `c${id}`),
        ...asArray(rule.treatment_rule_items).map((id) => `i${id}`),
    ];
    if (rule.treatment_rule_mode !== TREATMENT_RULE_MODE.ALL && ids.length) {
        const names = treatmentNameById
            ? ids.map((key) => treatmentNameById[key]).filter(Boolean)
            : [];
        const shown = names.length ? names.join(', ') : `${ids.length} selected`;
        lines.push(rule.treatment_rule_mode === TREATMENT_RULE_MODE.ALLOW
            ? `Only patients whose treatment is: ${shown}.`
            : `Never sent to patients whose treatment is: ${shown}.`);
        if (rule.treatment_rule_mode === TREATMENT_RULE_MODE.ALLOW) {
            lines.push(rule.send_when_treatment_unknown
                ? 'Patients with no treatment recorded still receive it.'
                : 'Patients with no treatment recorded do not receive it.');
        }
    }
    return lines.join(' ');
};

/**
 * Configured ids that no longer exist in the practice's treatment catalogue.
 *
 * A treatment deleted after the rule was saved leaves a dangling id. Harmless
 * on a blacklist, but on a whitelist it can quietly make the rule unmatchable —
 * so the editor surfaces it rather than letting a template go silent.
 */
export const danglingTreatmentIds = (rule, { categoryIds = [], itemIds = [] } = {}) => {
    const knownCategories = new Set(categoryIds.map(Number));
    const knownItems = new Set(itemIds.map(Number));
    return {
        categories: asArray(rule?.treatment_rule_categories)
            .map(Number).filter((id) => !knownCategories.has(id)),
        items: asArray(rule?.treatment_rule_items)
            .map(Number).filter((id) => !knownItems.has(id)),
    };
};

/** Shape a rule for PATCH/POST. Mirrors the backend's coherence validation. */
export const toPayload = (rule) => {
    const value = toRuleValue(rule);
    if (value.audience_policy === AUDIENCE_POLICY.INHERIT) {
        return {
            audience_policy: AUDIENCE_POLICY.INHERIT,
            audience_healthcare_providers: [],
            treatment_rule_mode: TREATMENT_RULE_MODE.ALL,
            treatment_rule_categories: [],
            treatment_rule_items: [],
            send_when_treatment_unknown: false,
        };
    }
    if (value.treatment_rule_mode === TREATMENT_RULE_MODE.ALL) {
        return {
            ...value,
            treatment_rule_categories: [],
            treatment_rule_items: [],
            send_when_treatment_unknown: false,
        };
    }
    if (value.treatment_rule_mode === TREATMENT_RULE_MODE.BLOCK) {
        return { ...value, send_when_treatment_unknown: false };
    }
    return value;
};

/**
 * Client-side mirror of the backend's coherence rule, so the Save button can be
 * disabled instead of round-tripping to a 400.
 */
export const validateRule = (rule) => {
    const value = toRuleValue(rule);
    if (value.audience_policy === AUDIENCE_POLICY.INHERIT) return null;
    if (value.treatment_rule_mode === TREATMENT_RULE_MODE.ALL) return null;
    const count = value.treatment_rule_categories.length + value.treatment_rule_items.length;
    if (!count) return 'Pick at least one treatment or category for this rule.';
    return null;
};

/** Build a `{ c<id>|i<id>: name }` lookup from the practice treatments list. */
export const treatmentNameIndex = (practiceTreatmentResults) => {
    const index = {};
    (practiceTreatmentResults || []).forEach((row) => {
        const item = row?.treatment_item;
        if (!item) return;
        if (item.id != null) index[`i${item.id}`] = item.name;
        const category = item.treatment_category;
        if (category?.id != null) index[`c${category.id}`] = category.name;
    });
    return index;
};
